// Which printer each kind of print goes to, and how each thermal printer is
// set up. Printers belong to the computer, so this is stored per machine
// (localStorage), not on the server.

const STORAGE_KEY = "zotaPrintSettings";

// Common roll sizes; "Custom" lets any other width be typed in.
export const ROLL_PRESETS = [
  { id: "58", rollWidth: 58, printableWidth: 48, label: '2" roll (58 mm)' },
  { id: "76", rollWidth: 76, printableWidth: 64, label: '3" roll (76 mm)' },
  { id: "80", rollWidth: 80, printableWidth: 72, label: '3" roll (80 mm)' },
  { id: "112", rollWidth: 112, printableWidth: 104, label: '4" roll (112 mm)' },
];

export const DPI_OPTIONS = [
  { dpi: 203, label: "203 dpi (8 dots/mm) — most printers" },
  { dpi: 180, label: "180 dpi (7 dots/mm)" },
  { dpi: 300, label: "300 dpi (12 dots/mm)" },
];

// How one thermal printer is driven. Every value can be changed in Printer Setup.
export const DEFAULT_RECEIPT_PROFILE = {
  rollWidth: 80, // mm, physical paper
  printableWidth: 72, // mm the head can actually print
  dpi: 203,
  offsetMm: 0, // shift the print left (-) or right (+) if the head isn't centred
  darkness: 150, // 0-255: pixels darker than this print black; higher = darker print
  mode: "escpos", // "escpos": raw ESC/POS image, no driver needed; "driver": through the printer driver
  imageMode: "column", // ESC/POS image command: "column" (ESC *, universal) or "raster" (GS v 0)
  stripSpacing: 24, // ESC * only: paper feed between 24-dot strips; lower it if white lines cross the text
  trimBlank: true, // remove blank space above and below the receipt
  topSpaceMm: "", // space before the receipt ("" = none when trimming, else the format's margin)
  bottomSpaceMm: "", // space after the receipt, before the feed / cut
  fit: "shrink", // format wider than the head: "shrink" to fit, or "actual" = exact font sizes (edges cut)
  cut: "partial", // "partial" | "full" | "none"
  feedLines: 1, // extra lines (24 dots each) fed before cutting; the cutter already moves to its cut position
  openDrawer: false, // kick the cash drawer after printing
};

export const DEFAULT_PRINT_SETTINGS = {
  documentPrinter: "", // A4 / A5 / Letter / custom sheets
  receiptPrinter: "", // default thermal roll printer
  profiles: {}, // { [printerName]: receipt profile }
  overrides: {}, // { [docType]: printerName }
  warnBeforePrint: true, // show mismatches before printing
  browserFallback: true, // no QZ Tray / desktop app -> browser print dialog
};

const LEGACY_KEYS = {
  receiptRollWidth: "rollWidth",
  receiptPrintableWidth: "printableWidth",
  receiptMode: "mode",
  receiptImageMode: "imageMode",
};

// Settings saved by the first version of Printer Setup had one set of receipt
// values; they become the profile of the receipt printer.
function migrate(saved) {
  if (!saved || saved.profiles || !Object.keys(LEGACY_KEYS).some((k) => k in saved)) return saved;
  const profile = {};
  Object.keys(LEGACY_KEYS).forEach((k) => {
    if (saved[k] !== undefined) profile[LEGACY_KEYS[k]] = saved[k];
    delete saved[k];
  });
  if (saved.receiptCut !== undefined) profile.cut = saved.receiptCut === false ? "none" : "partial";
  delete saved.receiptCut;
  saved.profiles = { [saved.receiptPrinter || ""]: profile };
  return saved;
}

export function getPrintSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const wasLegacy = !parsed.profiles && Object.keys(LEGACY_KEYS).some((k) => k in parsed);
    const settings = { ...DEFAULT_PRINT_SETTINGS, ...migrate(parsed) };
    if (wasLegacy) localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); // keep the converted form
    return settings;
  } catch {
    return { ...DEFAULT_PRINT_SETTINGS };
  }
}

export function savePrintSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...DEFAULT_PRINT_SETTINGS, ...settings }));
  } catch {}
}

// The full profile of a thermal printer ("" = the computer's default printer).
export function receiptProfile(settings, printerName) {
  const profiles = settings.profiles || {};
  return { ...DEFAULT_RECEIPT_PROFILE, ...(profiles[printerName || ""] || {}) };
}

// The printer a job should go to: per-document override, else by paper kind.
export function printerFor(settings, docType, isReceipt) {
  const override = docType && settings.overrides ? settings.overrides[docType] : "";
  if (override) return override;
  return isReceipt ? settings.receiptPrinter : settings.documentPrinter;
}
